import React from 'react';
import {Button, Card, Col, Flex, Popover, Progress, Row, Space, Statistic} from "antd";
import {getColor} from "../main/Analytics";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";

export const GrantData = ({loading, data}) => {
    const renderTitleWithPopover = (stat: any) => {
        const list = stat?.listOfPeople;
        if (Array.isArray(list) && list.length > 0) {
            return (
                <span>
                    {stat?.header}
                    <Popover
                        title="People"
                        content={<div style={{maxHeight: 200, overflowY: 'auto'}}>{list.map((name: string, i: number) => (<div key={`${name}-${i}`}>{name}</div>))}</div>}
                        trigger="click"
                    >
                        <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'pointer' }} />
                    </Popover>
                </span>
            );
        }
        return stat?.header;
    };

    const downloadCsv = () => {
        const rows: Array<[string, string, string | number, string]> = [];
        const addRow = (section: string, metric: string, value: any, person: string = '') => {
            rows.push([section, metric, value ?? '', person]);
        };
        const get = (obj: any, path: string[]) => path.reduce((acc, key) => (acc ? acc[key] : undefined), obj);

        // Contacts by Age
        if (data?.contactsByAge?.value) {
            Object.keys(data.contactsByAge.value).forEach((k) => {
                addRow(data.contactsByAge.header || 'Contacts by Age', k, data.contactsByAge.value[k]);
            });
        }

        // Contacts by Race
        if (Array.isArray(data?.contactsByRace?.value)) {
            data.contactsByRace.value.forEach((item: any) => {
                addRow(data.contactsByRace.header || 'Contacts by Race', item.name, item.value);
            });
        }

        // Contacts by Gender
        if (Array.isArray(data?.contactsByGender?.value)) {
            data.contactsByGender.value.forEach((item: any) => {
                addRow(data.contactsByGender.header || 'Contacts by Gender', item.name, item.value);
            });
        }

        // Contacts by So
        if (data?.contactsBySexualOrientation?.value) {
            Object.keys(data.contactsBySexualOrientation.value).forEach((k) => {
                addRow(data.contactsBySexualOrientation.header || 'Contacts by Sexual Orientation', k, data.contactsBySexualOrientation.value[k]);
            });
        }

        // Contacts by Education Level
        if (Array.isArray(data?.contactsByEducationLevel?.value)) {
            data.contactsByEducationLevel.value.forEach((item: any) => {
                addRow(data.contactsByEducationLevel.header || 'Contacts by Education Level', item.name, item.value);
            });
        }

        // Contacts by Parenting
        if (Array.isArray(data?.contactsByParenting?.value)) {
            data.contactsByParenting.value.forEach((item: any) => {
                addRow(data.contactsByParenting.header || 'Contacts by Parenting Data', item.name, item.value);
            });
        }

        // Unique section
        if (data?.uniqueContactsButNotPreviousRange) {
            addRow('Unique', data.uniqueContactsButNotPreviousRange.header, data.uniqueContactsButNotPreviousRange.value);
        }
        if (data?.uniqueContactsInPreviousRange) {
            addRow('Unique', data.uniqueContactsInPreviousRange.header, data.uniqueContactsInPreviousRange.value);
        }

        // Clinical Services
        if (data?.clinicalServiceContactsNot) {
            addRow('Clinical Services', data.clinicalServiceContactsNot.header, data.clinicalServiceContactsNot.value);
        }
        if (data?.clinicalServiceContactsIn) {
            addRow('Clinical Services', data.clinicalServiceContactsIn.header, data.clinicalServiceContactsIn.value);
        }

        // Financial Services (with optional person lists)
        const finNot = data?.financialServiceContactsNot;
        if (finNot) {
            addRow('Financial Services', finNot.header, finNot.value);
            if (Array.isArray(finNot.listOfPeople)) {
                finNot.listOfPeople.forEach((p: string) => addRow('Financial Services', finNot.header, '', p));
            }
        }
        const finIn = data?.financialServiceContactsIn;
        if (finIn) {
            addRow('Financial Services', finIn.header, finIn.value);
            if (Array.isArray(finIn.listOfPeople)) {
                finIn.listOfPeople.forEach((p: string) => addRow('Financial Services', finIn.header, '', p));
            }
        }

        // Housing Support
        if (data?.housingSupportContactsNot) {
            addRow('Housing Support', data.housingSupportContactsNot.header, data.housingSupportContactsNot.value);
        }
        if (data?.housingSupportContactsIn) {
            addRow('Housing Support', data.housingSupportContactsIn.header, data.housingSupportContactsIn.value);
        }

        // Served Through Youth Programming
        if (data?.loungeCheckInsNot) {
            addRow('Served Through Youth Programming', data.loungeCheckInsNot.header, data.loungeCheckInsNot.value);
        }
        if (data?.loungeCheckInsIn) {
            addRow('Served Through Youth Programming', data.loungeCheckInsIn.header, data.loungeCheckInsIn.value);
        }

        // Stats array (Contact Data de-duplicated)
        if (Array.isArray(data?.stats)) {
            data.stats.forEach((stat: any) => {
                addRow('Contact Data (De Duplicated)', stat.header, stat.value);
                if (Array.isArray(stat.listOfPeople)) {
                    stat.listOfPeople.forEach((p: string) => addRow('Contact Data (De Duplicated)', stat.header, '', p));
                }
            });
        }

        const csvEscape = (val: any) => {
            if (val === null || val === undefined) return '';
            const s = String(val);
            if (/[",\n]/.test(s)) {
                return '"' + s.replace(/"/g, '""') + '"';
            }
            return s;
        };

        const header = ['Section', 'Metric', 'Value', 'Person'];
        const csv = [header, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'grant-data.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <Col xs={24}>
            <Row justify={'end'} className={'mb-2'}>
                <Col>
                    <Button icon={<DownloadOutlined />} onClick={downloadCsv} disabled={loading}>Download CSV</Button>
                </Col>
            </Row>
            <Row gutter={[8, 8]}>
                <Col xs={8}>
                    <Card title={data.contactsByAge?.header} loading={loading}>
                        {Object.keys(data.contactsByAge?.value || {}).map((key: any, idx: any) => (
                            <Progress
                                key={idx}
                                className={'mb-2'}
                                percent={(data.contactsByAge?.value[key] / (data.contactsByAge?.total || 100)) * 100}
                                format={() => `${key} - (${data.contactsByAge?.value[key]})`}
                                percentPosition={{align: 'start', type: 'outer'}}
                                size={['100%', 15]}
                                strokeColor={`#00000`}
                            />
                        ))}
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={data.contactsByRace?.header} loading={loading}>
                        {data.contactsByRace?.value.map((val: any, idx: any) => (
                            <Progress
                                key={idx}
                                className={'mb-2'}
                                percent={(val.value / (data.contactsByRace?.total || 100)) * 100}
                                format={() => `${val.name} - (${val.value})`}
                                percentPosition={{align: 'start', type: 'outer'}}
                                size={['100%', 15]}
                                strokeColor={`#00000`}
                            />
                        ))}
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={data.contactsByGender?.header} loading={loading}>
                        {data.contactsByGender?.value.map((val: any, idx: any) => (
                            <Progress
                                key={idx}
                                className={'mb-2'}
                                percent={(val.value / (data.contactsByGender?.total || 100)) * 100}
                                format={() => `${val.name} - (${val.value})`}
                                percentPosition={{align: 'start', type: 'outer'}}
                                size={['100%', 15]}
                                strokeColor={`#00000`}
                            />
                        ))}
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={data.contactsBySexualOrientation?.header} loading={loading}>
                        {data.contactsBySexualOrientation?.value.map((val: any, idx: any) => (
                            <Progress
                                key={idx}
                                className={'mb-2'}
                                percent={(val.value / (data.contactsByGender?.total || 100)) * 100}
                                format={() => `${val.name} - (${val.value})`}
                                percentPosition={{align: 'start', type: 'outer'}}
                                size={['100%', 15]}
                                strokeColor={`#00000`}
                            />
                        ))}
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={data.contactsByParenting?.header} loading={loading}>
                        {data.contactsByParenting?.value.map((val: any, idx: any) => (
                            <Progress
                                key={idx}
                                className={'mb-2'}
                                percent={(val.value / (data.contactsByParenting?.total || 100)) * 100}
                                format={() => `${val.name} - (${val.value})`}
                                percentPosition={{align: 'start', type: 'outer'}}
                                size={['100%', 15]}
                                strokeColor={`#00000`}
                            />
                        ))}
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={data.contactsByEducationLevel?.header} loading={loading}>
                        {data.contactsByEducationLevel?.value.map((val: any, idx: any) => (
                            <Progress
                                key={idx}
                                className={'mb-2'}
                                percent={(val.value / (data.contactsByEducationLevel?.total || 100)) * 100}
                                format={() => `${val.name} - (${val.value})`}
                                percentPosition={{align: 'start', type: 'outer'}}
                                size={['100%', 15]}
                                strokeColor={`#00000`}
                            />
                        ))}
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={'All Services'} loading={loading}>
                        <Space size={'large'} align={'center'}>
                            <Statistic
                                title={data.uniqueContactsButNotPreviousRange?.header}
                                value={data.uniqueContactsButNotPreviousRange?.value}
                                valueStyle={{color: getColor(data.uniqueContactsButNotPreviousRange?.value)}}
                            />
                            <Statistic
                                title={data.uniqueContactsInPreviousRange?.header}
                                value={data.uniqueContactsInPreviousRange?.value}
                                valueStyle={{color: getColor(data.uniqueContactsInPreviousRange?.value)}}
                            />
                        </Space>
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={'Clinical Services'} loading={loading}>
                        <Space size={'large'} align={'center'}>
                            <Statistic
                                title={data.clinicalServiceContactsNot?.header}
                                value={data.clinicalServiceContactsNot?.value}
                                valueStyle={{color: getColor(data.clinicalServiceContactsNot?.value)}}
                            />
                            <Statistic
                                title={data.clinicalServiceContactsIn?.header}
                                value={data.clinicalServiceContactsIn?.value}
                                valueStyle={{color: getColor(data.clinicalServiceContactsIn?.value)}}
                            />
                        </Space>
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={'Financial Services'} loading={loading}>
                        <Space size={'large'} align={'center'}>
                            <Statistic
                                title={renderTitleWithPopover(data.financialServiceContactsNot)}
                                value={data.financialServiceContactsNot?.value}
                                valueStyle={{color: getColor(data.financialServiceContactsNot?.value)}}
                            />
                            <Statistic
                                title={renderTitleWithPopover(data.financialServiceContactsIn)}
                                value={data.financialServiceContactsIn?.value}
                                valueStyle={{color: getColor(data.financialServiceContactsIn?.value)}}
                            />
                        </Space>
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={'Housing Services'} loading={loading}>
                        <Space size={'large'} align={'center'}>
                            <Statistic
                                title={data.housingSupportContactsNot?.header}
                                value={data.housingSupportContactsNot?.value}
                                valueStyle={{color: getColor(data.housingSupportContactsNot?.value)}}
                            />
                            <Statistic
                                title={data.housingSupportContactsIn?.header}
                                value={data.housingSupportContactsIn?.value}
                                valueStyle={{color: getColor(data.housingSupportContactsIn?.value)}}
                            />
                        </Space>
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card title={'Programming'} loading={loading}>
                        <Space size={'large'} align={'center'}>
                            <Statistic
                                title={data.loungeCheckInsNot?.header}
                                value={data.loungeCheckInsNot?.value}
                                valueStyle={{color: getColor(data.loungeCheckInsNot?.value)}}
                            />
                            <Statistic
                                title={data.loungeCheckInsIn?.header}
                                value={data.loungeCheckInsIn?.value}
                                valueStyle={{color: getColor(data.loungeCheckInsIn?.value)}}
                            />
                        </Space>
                    </Card>
                </Col>
                <Col xs={24}>
                    <Card title={'Contact Data (De Duplicated)'} loading={loading}>
                        <Flex gap={'small'} wrap align={'flex-start'} justify={'flex-start'}>
                            {data.stats?.map((stat: any, idx: any) => (
                                <Card key={idx} styles={{body: {padding: '8px', textAlign: 'center'}}}>
                                    <Statistic
                                        title={renderTitleWithPopover(stat)}
                                        value={stat?.value}
                                        valueStyle={{color: getColor(stat?.value)}}
                                    />
                                </Card>
                            ))}
                        </Flex>
                    </Card>
                </Col>
            </Row>
        </Col>
    )
}